import { Skeleton } from "@mantine/core";
import {
  IconArrowRight,
  IconArrowUpRight,
  IconCheck,
  IconCircleCheck,
  IconHeartHandshake,
} from "@tabler/icons-react";
import { useId, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router";
import { Ring, Sparkline, TrendChart } from "./DashboardCharts";
import type {
  DashboardBreakdown,
  DashboardData,
  DashboardFeature,
  DashboardJourney,
  DashboardList,
  DashboardListItem,
  DashboardMetric,
  DashboardPrimary,
  DashboardTrend,
} from "./types";
import { DISPLAY_LOCALE } from "../format/dates";

/*
 * The home dashboard in the Horizon design: what is waiting, then the figures, the charts and the lists, each on a
 * white card. The greeting is the page's h1, for screen readers only. Every app fills the same `DashboardData` from its own queries.
 */

const count = new Intl.NumberFormat(DISPLAY_LOCALE);

function MetricValue({ metric, className }: { metric: DashboardMetric; className: string }) {
  if (metric.loading === true) return <Skeleton height={30} width={88} radius="md" />;
  if (metric.value === null) {
    return (
      <span className={className}>
        <span aria-hidden>—</span>
        <span className="how-visually-hidden">Not available</span>
      </span>
    );
  }
  return <span className={`${className} how-numeric`}>{count.format(metric.value)}</span>;
}

function sparkLabel(metric: DashboardMetric): string {
  return `${metric.label}, recent: ${(metric.series ?? []).join(", ")}`;
}

/** A metric as a tile: icon chip, label, value, hint and, where there is a real series, its sparkline. */
function MetricTile({ metric }: { metric: DashboardMetric }) {
  const body = (
    <>
      <span className="pv-tile__head">
        <span className="pv-tile__icon" aria-hidden>
          <metric.icon size={18} stroke={1.8} />
        </span>
        <span className="pv-tile__label">{metric.label}</span>
        {metric.to === undefined ? null : (
          <IconArrowUpRight className="pv-tile__go" size={16} stroke={1.8} aria-hidden />
        )}
      </span>
      <MetricValue metric={metric} className="pv-tile__value how-metric-value" />
      {metric.hint === undefined ? null : <span className="pv-tile__hint">{metric.hint}</span>}
      {metric.series !== undefined && metric.series.length > 1 ? (
        <span className="pv-tile__spark">
          <Sparkline values={metric.series} label={sparkLabel(metric)} />
        </span>
      ) : null}
    </>
  );
  return metric.to === undefined ? (
    <div className="pv-tile" data-tone={metric.tone}>
      {body}
    </div>
  ) : (
    <Link to={metric.to} className="pv-tile" data-tone={metric.tone}>
      {body}
    </Link>
  );
}

function Panel({
  title,
  caption,
  action,
  className,
  children,
}: {
  title: string;
  caption?: string | undefined;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section className={`pv-panel ${className ?? ""}`} aria-labelledby={id}>
      <header className="pv-panel__head">
        <div style={{ minWidth: 0 }}>
          <h2 id={id} className="pv-panel__title">
            {title}
          </h2>
          {caption === undefined ? null : <p className="pv-panel__caption">{caption}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function TrendPanel({ trend }: { trend: DashboardTrend }) {
  return (
    <Panel title={trend.title} caption={trend.caption} className="pv-panel--trend">
      {trend.loading === true ? (
        <Skeleton height={220} radius="lg" />
      ) : trend.points.length === 0 ? (
        <p className="pv-empty">Nothing to chart yet.</p>
      ) : (
        <TrendChart points={trend.points} unit={trend.unit} />
      )}
    </Panel>
  );
}

function BreakdownPanel({ breakdown }: { breakdown: DashboardBreakdown }) {
  const total = breakdown.parts.reduce((sum, p) => sum + p.value, 0);
  return (
    <Panel title={breakdown.title} caption={breakdown.caption} className="pv-panel--breakdown">
      {breakdown.loading === true ? (
        <Skeleton height={180} radius="lg" />
      ) : breakdown.parts.length === 0 ? (
        <p className="pv-empty">Nothing to show yet.</p>
      ) : (
        <div className="pv-breakdown">
          <Ring parts={breakdown.parts} totalLabel={breakdown.totalLabel} />
          <ul className="pv-legend">
            {breakdown.parts.map((p, i) => (
              <li key={p.label}>
                <span
                  className="pv-legend__swatch"
                  style={{ background: `var(--pv-part-${String(i % 5)})` }}
                  aria-hidden
                />
                <span className="pv-legend__label">{p.label}</span>
                <span className="pv-legend__value how-numeric">
                  {count.format(p.value)}
                  <span className="pv-legend__share">
                    {total === 0 ? "" : ` · ${String(Math.round((p.value / total) * 100))}%`}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

function initials(title: string): string {
  const words = title.split(/\s+/).filter((w) => /^[\p{L}\p{N}]/u.test(w));
  return words
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("");
}

function ItemText({ item }: { item: DashboardListItem }) {
  return (
    <span className="pv-item__text">
      <span className="pv-item__title">{item.title}</span>
      {item.meta === undefined ? null : <span className="pv-item__meta">{item.meta}</span>}
    </span>
  );
}

function ListPanel({ list }: { list: DashboardList }) {
  const more =
    list.more === undefined ? null : (
      <Link to={list.more.to} className="pv-more">
        {list.more.label}
        <IconArrowUpRight size={14} stroke={2} aria-hidden />
      </Link>
    );
  let body: ReactNode;
  if (list.loading === true) {
    body = (
      <div className="pv-stack" style={{ gap: 10 }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} height={40} radius="md" />
        ))}
      </div>
    );
  } else if (list.items.length === 0) {
    body = <p className="pv-empty">{list.empty}</p>;
  } else {
    body = (
      <ul className="pv-list pv-list--rows">
        {list.items.map((item) => (
          <li key={item.id} className="pv-item">
            <span className="pv-item__avatar" aria-hidden>
              {initials(item.title)}
            </span>
            <ItemText item={item} />
            <span className="pv-item__end">
              {item.when === undefined ? null : <span className="pv-item__when">{item.when}</span>}
              {item.badge === undefined ? null : <span className="pv-badge">{item.badge}</span>}
            </span>
          </li>
        ))}
      </ul>
    );
  }
  return (
    <Panel title={list.title} caption={list.caption} action={more} className="pv-panel--list">
      {body}
    </Panel>
  );
}

function FactsPanel({ facts }: { facts: NonNullable<DashboardData["facts"]> }) {
  return (
    <Panel title={facts.title} className="pv-panel--facts">
      <dl className="pv-facts">
        {facts.rows.map((row) => (
          <div key={row.label} className="pv-facts__row">
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
    </Panel>
  );
}

/** The page's main view, on its own card, first on the page: what is waiting for the reader, each item with its verb. */
function PrimaryPanel({ primary }: { primary: DashboardPrimary }) {
  const id = useId();
  const count = primary.items.length;
  return (
    <section className="pv-tasks" aria-labelledby={id}>
      <header className="pv-tasks__head">
        <h2 id={id} className="pv-tasks__title">
          {primary.title}
          {count === 0 ? null : (
            <span className="pv-tasks__count">
              {count}
              <span className="how-visually-hidden"> waiting</span>
            </span>
          )}
        </h2>
        {primary.caption === undefined ? null : (
          <p className="pv-tasks__caption">{primary.caption}</p>
        )}
      </header>
      {primary.loading === true && count === 0 ? (
        <div className="pv-tasks__list">
          {[0, 1].map((i) => (
            <Skeleton key={i} height={56} radius="lg" className="pv-tasks__skeleton" />
          ))}
        </div>
      ) : count === 0 ? (
        <p className="pv-tasks__empty">
          <IconCircleCheck size={20} stroke={1.8} aria-hidden />
          {primary.empty}
        </p>
      ) : (
        <ul className="pv-tasks__list">
          {primary.items.map((t) => (
            <li key={t.key}>
              <Link to={t.to} className="pv-task" data-tone={t.tone}>
                <span className="pv-task__icon" aria-hidden>
                  <t.icon size={18} stroke={1.8} />
                </span>
                <span className="pv-task__text">{t.text}</span>
                <span className="pv-task__action">
                  {t.action}
                  <IconArrowRight size={14} stroke={2} aria-hidden />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * The reader's way to membership, full width right after what is waiting: the standing in words, how far along, every
 * step on one rail, and the next step drawn large with what the church wrote about it. Once the reader is a member
 * it says so, briefly, instead.
 */
function JourneyPanel({ journey }: { journey: DashboardJourney }) {
  const id = useId();
  const steps = journey.steps;
  const done = steps.filter((s) => s.doneOn !== undefined).length;
  const next = steps.find((s) => s.doneOn === undefined);
  if (journey.loading === true) {
    return (
      <section className="pv-journey" aria-labelledby={id} aria-busy>
        <h2 id={id} className="pv-journey__eyebrow">
          {journey.title}
        </h2>
        <Skeleton height={150} radius="lg" className="pv-journey__skeleton" />
      </section>
    );
  }
  if (journey.member !== undefined) {
    return (
      <section className="pv-journey pv-journey--member" aria-labelledby={id}>
        <span className="pv-journey__seal" aria-hidden>
          <IconHeartHandshake size={28} stroke={1.6} />
        </span>
        <div className="pv-journey__welcome">
          <h2 id={id} className="pv-journey__heading">
            You are a member
          </h2>
          {journey.member.since === undefined ? null : (
            <p className="pv-journey__standing">Since {journey.member.since}.</p>
          )}
        </div>
      </section>
    );
  }
  return (
    <section className="pv-journey" aria-labelledby={id}>
      <header className="pv-journey__head">
        <div style={{ minWidth: 0 }}>
          <h2 id={id} className="pv-journey__eyebrow">
            {journey.title}
          </h2>
          <p className="pv-journey__heading">{journey.standing ?? "Your way to membership"}</p>
          {journey.intro === undefined ? null : (
            <p className="pv-journey__intro">{journey.intro}</p>
          )}
        </div>
        {steps.length === 0 ? null : (
          <div className="pv-journey__progress">
            <span className="pv-journey__count how-numeric">
              {done}
              <span className="pv-journey__of"> of {steps.length}</span>
            </span>
            <span className="pv-journey__count-label">steps done</span>
            <span className="pv-journey__bar" aria-hidden>
              <span style={{ width: `${String((done / steps.length) * 100)}%` }} />
            </span>
          </div>
        )}
      </header>
      {steps.length === 0 ? null : (
        <ol className="pv-journey__steps" style={{ "--pv-steps": steps.length } as CSSProperties}>
          {steps.map((s, i) => {
            const state = s.doneOn !== undefined ? "done" : s === next ? "next" : "ahead";
            return (
              <li
                key={s.key}
                className="pv-step"
                data-state={state}
                {...(state === "next" ? { "aria-current": "step" as const } : {})}
              >
                <span className="pv-step__marker" aria-hidden>
                  {state === "done" ? <IconCheck size={16} stroke={2.6} /> : i + 1}
                </span>
                <span className="pv-step__name">{s.name}</span>
                <span className="pv-step__state">
                  {state === "done"
                    ? `Done ${s.doneOn ?? ""}`
                    : state === "next"
                      ? "Next"
                      : "Ahead"}
                </span>
              </li>
            );
          })}
        </ol>
      )}
      {next === undefined ? (
        steps.length === 0 ? null : (
          <p className="pv-journey__all-done">
            <IconCircleCheck size={20} stroke={1.8} aria-hidden />
            Every step is done.
          </p>
        )
      ) : (
        <div className="pv-journey__next">
          <span className="pv-journey__next-label">
            Your next step
            <IconArrowRight size={14} stroke={2.2} aria-hidden />
          </span>
          <h3 className="pv-journey__next-name">{next.name}</h3>
          {next.description === undefined ? null : (
            <p className="pv-journey__next-text">{next.description}</p>
          )}
        </div>
      )}
      {journey.note === undefined ? null : <p className="pv-journey__note">{journey.note}</p>}
    </section>
  );
}

/** One thing on its own card (the latest sermon), in the place a trend chart would take. */
function FeaturePanel({ feature }: { feature: DashboardFeature }) {
  return (
    <Panel
      title={feature.title}
      className="pv-panel--feature"
      action={feature.tag === undefined ? null : <span className="pv-badge">{feature.tag}</span>}
    >
      {feature.loading === true ? (
        <Skeleton height={160} radius="lg" />
      ) : (
        <div className="pv-feature">
          <span className="pv-feature__art" aria-hidden>
            <feature.icon size={40} stroke={1.4} />
          </span>
          <div className="pv-feature__body">
            <h3 className="pv-feature__heading">{feature.heading}</h3>
            {feature.meta.length === 0 ? null : (
              <p className="pv-feature__meta">{feature.meta.join(" · ")}</p>
            )}
            {feature.body === undefined ? null : <p className="pv-feature__text">{feature.body}</p>}
            <Link to={feature.action.to} className="pv-action" data-primary>
              {feature.action.label}
              <IconArrowRight size={16} stroke={2} aria-hidden />
            </Link>
          </div>
        </div>
      )}
    </Panel>
  );
}

/** The way to membership when the app gives one. */
function Journey({ data }: { data: DashboardData }) {
  if (data.journey === undefined || data.journey === null) return null;
  return <JourneyPanel journey={data.journey} />;
}

/** The feature when there is one, the trend chart otherwise, or nothing. */
function ChartSlot({ data }: { data: DashboardData }) {
  if (data.feature !== undefined && data.feature !== null)
    return <FeaturePanel feature={data.feature} />;
  if (data.trend !== null) return <TrendPanel trend={data.trend} />;
  return null;
}

/** The facts and any extra card, stacked; nothing at all when there are neither, so the list takes the row. */
function SideStack({ data }: { data: DashboardData }) {
  if (data.facts === null && data.extra === undefined) return null;
  return (
    <div className="pv-stack">
      {data.facts === null ? null : <FactsPanel facts={data.facts} />}
      {data.extra}
    </div>
  );
}

export function HorizonLayout({ data }: { data: DashboardData }) {
  return (
    <div className="pv-layout pv-horizon">
      {/*
       * The page opens on its figures (owner, 2026-10-13: no banner, then no greeting or shortcut buttons either). The
       * greeting stays as the page's h1 for screen readers, so the outline still starts at its own heading (#302).
       */}
      <h1 className="how-visually-hidden">{data.greeting}</h1>
      {data.primary === undefined ? null : <PrimaryPanel primary={data.primary} />}
      <Journey data={data} />
      <div className="pv-grid pv-grid--tiles4">
        {data.metrics.map((m) => (
          <MetricTile key={m.key} metric={m} />
        ))}
      </div>
      <div className="pv-grid pv-grid--main">
        <ChartSlot data={data} />
        {data.breakdown === null ? null : <BreakdownPanel breakdown={data.breakdown} />}
      </div>
      <div className="pv-grid pv-grid--main">
        <ListPanel list={data.list} />
        <SideStack data={data} />
      </div>
    </div>
  );
}
